import React, { createContext, useState, useEffect, useRef } from 'react';
import { authAPI, settingsAPI } from '../services/api';
import { notify, registerSessionExpired } from '../utils/notify';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    loadUser();
  }, []);

  useEffect(() => {
    registerSessionExpired(() => {
      if (timerRef.current) clearTimeout(timerRef.current);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      if (window.location.pathname !== '/login') {
        notify('Session expired. Please sign in again.', 'warning');
        window.location.href = '/login';
      }
    });
    return () => registerSessionExpired(null);
  }, []);

  useEffect(() => {
    if (user) {
      setupInactivityDetection();
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [user]);

  const setupInactivityDetection = () => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const savedMinutes = localStorage.getItem('session_timeout_minutes');
      const timeoutMs = savedMinutes ? parseInt(savedMinutes) * 60 * 1000 : 1800000;
      timerRef.current = setTimeout(() => {
        handleInactivityLogout();
      }, timeoutMs);
    };

    events.forEach(event => window.addEventListener(event, resetTimer));
    resetTimer();

    return () => {
      events.forEach(event => window.removeEventListener(event, resetTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  };

  const handleInactivityLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Error during inactivity logout:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      window.location.href = '/login';
    }
  };

  const loadUser = async () => {
  try {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token) {
      if (savedUser) setUser(JSON.parse(savedUser));
      const response = await authAPI.getCurrentUser();
      setUser(response.data.user);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Cargar session timeout desde el backend al iniciar. Not for a guard
      // (role employee): the API refuses them the settings, and the portal
      // keeps the default timeout.
      if (response.data.user && response.data.user.role !== 'employee') {
        try {
          const settingsResponse = await settingsAPI.getAll();
          const settingsData = settingsResponse.data;
          if (settingsData.settings) {
            const timeoutSetting = settingsData.settings.find(s => s.setting_key === 'session_timeout_minutes');
            if (timeoutSetting) {
              localStorage.setItem('session_timeout_minutes', timeoutSetting.setting_value);
            }
          }
        } catch (e) {
          console.error('Error loading session timeout:', e);
        }
      }

    }
  } catch (error) {
    console.error('Error loading user:', error);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  } finally {
    setLoading(false);
  }
};
  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error.response?.data);
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed'
      };
    }
  };

  const logout = async () => {
    try {
      if (timerRef.current) clearTimeout(timerRef.current);
      await authAPI.logout();
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      window.location.href = '/login';
    }
  };

  const updateUser = (updatedUser) => setUser(updatedUser);

  const refreshPermissions = async () => {
    try {
      const response = await authAPI.getCurrentUser();
      setUser(response.data.user);
      console.log('✅ Permisos actualizados correctamente');
      return { success: true };
    } catch (error) {
      console.error('Error refreshing permissions:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Error refreshing permissions'
      };
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateUser, loadUser, refreshPermissions }}>
      {children}
    </AuthContext.Provider>
  );
};